import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCompact } from '@/lib/money';
import type { TimeSlicedStatisticsDTO } from '@/api/generated/model';
import { useEngagementCounts, useEngagementSeries, useTopContentPreview } from '../api';
import { ENGAGEMENT_SERIES_COLORS, FALLBACK_SERIES_COLOR } from '@/lib/chartTheme';
import { MetricCardsRow, type MetricCardItem } from './MetricCardsRow';
import { EngagementChart, type EngagementSeriesDatum } from '@/components/charts/EngagementChart';
import { EngagementBreakdown } from './EngagementBreakdown';
import { TopContentGrid } from './TopContentGrid';
import { RangePresetPicker, useRangePreset } from '@/components/charts/RangePresetPicker';

/**
 * headers[0] is always "timeSliceMedianTime" (StatisticsServiceImpl.ENGAGEMENT_HEADERS); every
 * other header names one series (Purchases/Likes/Comments/Tips). Matched by name, not a fixed
 * index, so an unexpected header still renders (with the muted fallback color) instead of
 * silently vanishing.
 */
function parseEngagementSeries(dto: TimeSlicedStatisticsDTO | undefined): {
  categories: string[];
  series: EngagementSeriesDatum[];
} {
  const headers = dto?.headers ?? [];
  const rows = dto?.rows ?? [];
  const dateIdx = headers.findIndex((h) => h.toLowerCase().includes('time'));
  const resolvedDateIdx = dateIdx >= 0 ? dateIdx : 0;
  const categories = rows.map((r) => r[resolvedDateIdx] ?? '');
  const series = headers
    .map((name, index) => ({ name, index }))
    .filter(({ index }) => index !== resolvedDateIdx)
    .map(({ name, index }) => {
      const data = rows.map((r) => Number(r[index] ?? '0') || 0);
      return {
        name,
        color: ENGAGEMENT_SERIES_COLORS[name] ?? FALLBACK_SERIES_COLOR,
        data,
        total: data.reduce((a, b) => a + b, 0),
      };
    });
  return { categories, series };
}

/**
 * Engagement section for the user detail page — real port of CMP's `StatisticsEngagementView`
 * scoped to what the admin API actually exposes: a Purchases/Likes/Comments/Tips line chart
 * (op #9), a breakdown of the same counts, visits/subscribers + total-content metric cards
 * (op #10 + top-content totals), and a Top Content preview row (op #6).
 */
export function EngagementSection({ userId }: { userId: string }) {
  const { days, setDays, startISO, endISO } = useRangePreset(30);
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});

  const engagementQuery = useEngagementSeries(userId, {
    requestDTO: { endDate: endISO, timeSliceSize: 'DAY', timeSliceCount: days },
  });
  const countsQuery = useEngagementCounts(userId, {
    requestDTO: { startDateTime: startISO, endDateTime: endISO },
  });
  const topContentQuery = useTopContentPreview(userId, 8);

  const { categories, series } = useMemo(
    () => parseEngagementSeries(engagementQuery.data),
    [engagementQuery.data],
  );

  const visits = countsQuery.data?.['visits'];
  const subscribers = countsQuery.data?.['subscribers'];
  const totalContents = topContentQuery.data?.totalElements;

  // Real counts only — no "Total Received" card here (CMP hardcoded that one as a demo value):
  // it would need its own date range to be honest, and that lives in the Earnings section below.
  const metrics: MetricCardItem[] = [
    { label: 'Total Visits', value: visits != null ? formatCompact(Number(visits) || 0) : '—' },
    {
      label: 'New Subscribers',
      value: subscribers != null ? formatCompact(Number(subscribers) || 0) : '—',
    },
    {
      label: 'Total Contents',
      value: totalContents != null ? formatCompact(totalContents) : '—',
    },
  ];

  const isLoading = engagementQuery.isLoading || countsQuery.isLoading;
  const isError = engagementQuery.isError || countsQuery.isError;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Engagement</CardTitle>
        <RangePresetPicker days={days} onChange={setDays} />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <MetricCardsRow metrics={metrics} />

        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-destructive">
            Failed to load engagement statistics.
          </p>
        ) : (
          <>
            <EngagementChart
              categories={categories}
              series={series}
              hiddenSeries={hiddenSeries}
              onLegendChange={setHiddenSeries}
            />
            <EngagementBreakdown
              series={series}
              hiddenSeries={hiddenSeries}
              onToggle={(name) =>
                setHiddenSeries((prev) => ({ ...prev, [name]: !prev[name] }))
              }
            />
          </>
        )}

        <TopContentGrid
          posts={topContentQuery.data?.rows ?? []}
          isLoading={topContentQuery.isLoading}
          isError={topContentQuery.isError}
        />
      </CardContent>
    </Card>
  );
}
